import { type ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { type Maybe, getAgeGroup } from '@shared/utils/src';
import { type CentPrecisionPriceRanges, Locale, useLocaleContext } from '@storefront/lib-global';
import { sendApplyProductVariantEvent } from '@storefront/lib-global/utils/gtm/events/apply_product_variant/sendApplyProductVariantEvent';
import type { ProductVariantTypes } from '../../types';
import {
  areAllVariantsOutOfStock,
  areAllVariantsWithNotificationsDisabled,
  hasBackInStockFlag,
  isOutOfStock,
} from '../../utils';
import { SizeAndWidthContext } from './sizeAndWidthContext';

type SizeAndWidthContextProviderProps = ProductVariantTypes & {
  children: ReactNode;
  priceRanges?: Maybe<CentPrecisionPriceRanges>;
  initialWidth: string;
  initialSize: string;
};

const localesWithPreselect = [Locale.EN_US, Locale.EN_CA, Locale.FR_CA];

const preselectValue = (values: string[], preselectMedium?: boolean) => {
  const findMedium = preselectMedium ? values.find((value) => value.toLowerCase() === 'medium') ?? '' : '';

  return values.length === 1 ? values[0] : findMedium;
};

export function SizeAndWidthContextProvider({
  children,
  uniqueWidths,
  uniqueSizes,
  variants,
  priceRanges,
  initialWidth,
  initialSize,
}: SizeAndWidthContextProviderProps) {
  const { locale } = useLocaleContext();
  const currentLocale = locale ?? Locale.DEFAULT_LOCALE;
  const preselectedWidthValue = preselectValue(uniqueWidths, localesWithPreselect.includes(currentLocale));
  const preselectedSizeValue = preselectValue(uniqueSizes);

  const [selectedWidth, setSelectedWidth] = useState(preselectedWidthValue);
  const [selectedSize, setSelectedSize] = useState(preselectedSizeValue);
  const [selectedSku, setSelectedSku] = useState(
    uniqueSizes.length > 1 || uniqueWidths.length > 1 ? '' : variants[0]?.sku,
  );
  const [error, setError] = useState(['']);
  const isSubdomainOutlet = locale === Locale.STOREFRONT_OUTLET_LOCALE;

  const [isNotifyMeOpen, setNotifyMeOpen] = useState(false);
  const [noResultsFound, setNoResultsFound] = useState(false);

  const setNewSku = useCallback(
    (skuSize: string, skuWidth: string) => {
      const sku = variants.find(
        (variant) =>
          (skuSize ? variant.size === skuSize : variant.size === null) &&
          (skuWidth ? variant.fitLabel === skuWidth : variant.fitLabel === null),
      );

      setSelectedSku(sku?.sku ?? '');
      return sku?.sku ?? '';
    },
    [variants],
  );

  const handleChangeSize = useCallback(
    (newSize: string) => () => {
      setSelectedSize(newSize);
      const sku = setNewSku(newSize, selectedWidth);
      setError((prev) => (prev.includes('width') ? ['width'] : ['']));
      return sku;
    },
    [selectedWidth, setNewSku],
  );

  const handleChangeWidth = useCallback(
    (newWidth: string) => () => {
      setSelectedWidth(newWidth);
      const sku = setNewSku(selectedSize, newWidth);
      setError((prev) => (prev.includes('size') ? ['size'] : ['']));
      return sku;
    },
    [selectedSize, setNewSku],
  );

  const setNotifyMeVisibility = useCallback((visibility: boolean) => {
    setNotifyMeOpen(visibility);
  }, []);

  const setNoStockResultsFound = useCallback((noResults: boolean) => {
    setNoResultsFound(noResults);
  }, []);

  const isProductSoldOutOnline =
    areAllVariantsOutOfStock(variants) && areAllVariantsWithNotificationsDisabled(variants);
  const isSelectedVariantOutOfStock = isOutOfStock(selectedSize, selectedWidth, variants);
  const isSelectedVariantWithNotificationEnabled = Boolean(
    selectedSize && selectedWidth && hasBackInStockFlag(selectedSize, selectedWidth, variants),
  );

  const sizeAndWidthContext = useMemo(
    () => ({
      selectedSize,
      handleChangeSize,
      selectedWidth,
      handleChangeWidth,
      selectedSku,
      error,
      setError,
      isSubdomainOutlet,
      uniqueWidths,
      uniqueSizes,
      variants,
      isNotifyMeOpen,
      setNotifyMeVisibility,
      priceRanges,
      noResultsFound,
      setNoStockResultsFound,
      isProductSoldOutOnline,
      isSelectedVariantOutOfStock,
      isSelectedVariantWithNotificationEnabled,
    }),
    [
      selectedSize,
      handleChangeSize,
      selectedWidth,
      handleChangeWidth,
      selectedSku,
      error,
      isSubdomainOutlet,
      uniqueWidths,
      uniqueSizes,
      variants,
      isNotifyMeOpen,
      setNotifyMeVisibility,
      priceRanges,
      noResultsFound,
      setNoStockResultsFound,
      isProductSoldOutOnline,
      isSelectedVariantOutOfStock,
      isSelectedVariantWithNotificationEnabled,
    ],
  );

  useEffect(() => {
    setSelectedSize(initialSize || preselectValue(uniqueSizes));
  }, [initialSize, uniqueSizes]);

  useEffect(() => {
    setSelectedWidth(initialWidth || preselectValue(uniqueWidths, localesWithPreselect.includes(currentLocale)));
  }, [initialWidth, uniqueWidths, currentLocale]);

  useEffect(() => {
    if (initialSize && initialWidth) {
      setNewSku(initialSize, initialWidth);
    } else {
      setSelectedSku(uniqueSizes.length > 1 || uniqueWidths.length > 1 ? '' : variants[0]?.sku);
    }
  }, [setNewSku, initialWidth, initialSize, uniqueSizes, uniqueWidths, variants]);

  useEffect(() => {
    setError([]);
  }, [uniqueSizes, uniqueWidths, selectedSize, selectedWidth]);

  useEffect(() => {
    const sku = variants.find(
      (variant) => variant.size === preselectedSizeValue && variant.fitLabel === preselectedWidthValue,
    );
    if (preselectedWidthValue) {
      sendApplyProductVariantEvent({
        variant_name: 'Fit',
        variant_value: `Fit ${preselectedWidthValue}`,
        item_sku: sku?.sku ?? '',
      });
    }

    if (preselectedSizeValue) {
      const globalSize = variants[0]?.globalSize || '';
      const ageGroup = getAgeGroup(globalSize);

      sendApplyProductVariantEvent({
        variant_name: 'Size',
        variant_value: `${
          ageGroup ? `${ageGroup.replace(/^\w/, (match) => match.toUpperCase())} size` : 'Size'
        } ${preselectedSizeValue}`,
        item_sku: sku?.sku ?? '',
      });
    }
  }, [preselectedWidthValue, preselectedSizeValue, variants]);

  return <SizeAndWidthContext.Provider value={sizeAndWidthContext}>{children}</SizeAndWidthContext.Provider>;
}
